import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Activity, 
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  Zap,
  Timer,
  Lock
} from 'lucide-react';

const GPS_METRICS_DISPLAY = {
  distance_m: "Distance (m)",
  distance_per_min: "Dist/min",
  shi_distance: "SHI Dist",
  s7_distance: "Sprint Dist",
  acchi_distance: "AccHI Dist",
  dechi_distance: "DecHI Dist",
  speed_max: "Speed Max",
  session_minutes: "Minutes",
  player_load: "Player Load",
  running_symmetry: "Run Symmetry",
  imbalance: "Imbalance",
};

const getEffectiveMinutes = (event) => {
  if (event.is_match && event.match_minutes != null) {
    return (event.match_minutes || 0) + (event.post_match_minutes || 0);
  }
  return event.metrics?.session_minutes || 0;
};

const AthleteGPSPage = () => {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('training_load');
  const [showEventDetail, setShowEventDetail] = useState(null);
  const [showRPEDialog, setShowRPEDialog] = useState(null);
  const [rpeValue, setRpeValue] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch events
      const eventsRes = await api.get('/gps/athlete/events?limit=50');
      setEvents(eventsRes.data);
      
      // Fetch summary
      const summaryRes = await api.get('/gps/athlete/summary');
      setSummary(summaryRes.data);
      
      // Fetch history for charts
      const historyRes = await api.get('/gps/athlete/history?limit=30');
      setHistory(historyRes.data);
      
    } catch (err) {
      console.error('Failed to fetch GPS data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitRPE = async () => {
    if (!rpeValue || parseInt(rpeValue) < 0 || parseInt(rpeValue) > 10) {
      toast.error(language === 'IT' ? 'Inserisci un valore RPE valido (0-10)' : 'Enter a valid RPE value (0-10)');
      return;
    }
    
    try {
      const eventId = showRPEDialog.event.event_id || showRPEDialog.event.id;
      await api.post(`/gps/athlete/events/${eventId}/rpe`, {
        rpe: parseInt(rpeValue)
      });
      toast.success(language === 'IT' ? 'RPE salvato!' : 'RPE saved!');
      setShowRPEDialog(null);
      setRpeValue('');
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || (language === 'IT' ? 'Errore nel salvataggio' : 'Failed to save');
      toast.error(msg);
    }
  };

  const canSubmitRPE = (event) => {
    // API returns data flat at event level, not inside athlete_row
    if (event.rpe_status === 'EXPIRED') return false;

    // Compute how many whole days have elapsed since the session date. Athletes
    // typically log RPE the day-of or the day-after. We accept a 7-day submission
    // window for MISSING events; after 7 days it becomes read-only (staff can
    // still backfill via /gps/rows/{row_id}/rpe).
    // NB: this is FRONTEND eligibility only — the backend accepts any submission.
    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const daysSince = Math.floor((today - eventDate) / (1000 * 60 * 60 * 24));

    // MISSING: submit is always allowed within a rolling window (past or today).
    // Future events (daysSince < 0) are allowed too — the athlete might be logging
    // early with the coach's approval.
    if (event.rpe_status === 'MISSING') {
      return daysSince <= 7;
    }
    // SUBMITTED: allow edit for 12 hours after submission.
    if (event.rpe_status === 'SUBMITTED' && event.rpe_submitted_at) {
      const hoursDiff = (Date.now() - new Date(event.rpe_submitted_at).getTime()) / (1000 * 60 * 60);
      return hoursDiff < 12;
    }
    return false;
  };

  const submissionBlockReason = (event) => {
    if (event.rpe_status === 'EXPIRED') {
      return language === 'IT' ? 'La finestra di inserimento è chiusa.' : 'The submission window is closed.';
    }
    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const daysSince = Math.floor((today - eventDate) / (1000 * 60 * 60 * 24));
    if (event.rpe_status === 'MISSING' && daysSince > 7) {
      return language === 'IT' ? 'La finestra di inserimento è chiusa (oltre 7 giorni).' : 'Submission window closed (>7 days).';
    }
    if (event.rpe_status === 'SUBMITTED') {
      return language === 'IT' ? 'RPE già inviato.' : 'RPE already submitted.';
    }
    return null;
  };

  const getStatusBadge = (event) => {
    // API returns data flat at event level
    const rpeStatus = event?.rpe_status;
    if (rpeStatus === 'SUBMITTED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400">
          <CheckCircle className="w-3 h-3" />
          {language === 'IT' ? 'Inviato' : 'Submitted'}
        </span>
      );
    }
    if (rpeStatus === 'EXPIRED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-rose-500/20 text-rose-400">
          <Lock className="w-3 h-3" />
          {language === 'IT' ? 'Scaduto' : 'Expired'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400">
        <Clock className="w-3 h-3" />
        {language === 'IT' ? 'Da inserire' : 'Missing'}
      </span>
    );
  };

  // Simple SVG chart for training load trend
  const renderTrendChart = () => {
    if (!history || history.length < 2) return null;
    
    const data = [...history].reverse().slice(-15);
    if (data.length < 2) return null;
    
    let values;
    let label;
    
    if (selectedMetric === 'training_load') {
      values = data.map(d => d.training_load || 0);
      label = language === 'IT' ? 'Training Load' : 'Training Load';
    } else if (selectedMetric === 'session_minutes') {
      values = data.map(d => getEffectiveMinutes(d) || 0);
      label = language === 'IT' ? 'Minuti' : 'Minutes';
    } else if (selectedMetric === 'speed_max') {
      values = data.map(d => d.metrics?.[selectedMetric] || d[selectedMetric] || 0);
      label = GPS_METRICS_DISPLAY[selectedMetric] || selectedMetric;
    } else {
      values = data.map(d => d.metrics?.[selectedMetric] || d[selectedMetric] || 0);
      label = GPS_METRICS_DISPLAY[selectedMetric] || selectedMetric;
    }
    
    const maxVal = Math.max(...values, 1);
    const minVal = Math.min(...values);
    const range = maxVal - minVal || 1;
    
    const width = 500;
    const height = 160;
    const padding = 30;
    
    const points = values.map((val, i) => {
      const x = padding + (i / (values.length - 1)) * (width - 2 * padding);
      const y = height - padding - ((val - minVal) / range) * (height - 2 * padding);
      return { x, y, val };
    });
    
    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    
    // Area fill
    const areaD = pathD + ` L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`;
    
    return (
      <div data-testid="trend-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-zinc-400">{label}</h3>
          <Select value={selectedMetric} onValueChange={setSelectedMetric}>
            <SelectTrigger className="w-40 h-8 bg-zinc-950 border-zinc-800 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="training_load">Training Load</SelectItem>
              <SelectItem value="session_minutes">{language === 'IT' ? 'Minuti' : 'Minutes'}</SelectItem>
              <SelectItem value="distance_m">Distance</SelectItem>
              <SelectItem value="distance_per_min">Dist/min</SelectItem>
              <SelectItem value="shi_distance">SHI Distance</SelectItem>
              <SelectItem value="s7_distance">Sprint Distance</SelectItem>
              <SelectItem value="acchi_distance">AccHI Distance</SelectItem>
              <SelectItem value="dechi_distance">DecHI Distance</SelectItem>
              <SelectItem value="speed_max">Speed Max</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          {/* Grid lines */}
          <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#3f3f46" strokeWidth="1" />
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#3f3f46" strokeWidth="1" />
          
          {/* Horizontal grid lines */}
          {[0.25, 0.5, 0.75].map((ratio, i) => (
            <line key={i} x1={padding} y1={padding + ratio * (height - 2 * padding)} x2={width - padding} y2={padding + ratio * (height - 2 * padding)} stroke="#27272a" strokeWidth="1" strokeDasharray="4 4" />
          ))}
          
          {/* Y-axis labels */}
          <text x="5" y={padding + 5} fill="#71717a" fontSize="10">{maxVal.toFixed(0)}</text>
          <text x="5" y={height - padding} fill="#71717a" fontSize="10">{minVal.toFixed(0)}</text>
          
          {/* Area fill */}
          <path d={areaD} fill="url(#areaGradient)" opacity="0.3" />
          
          {/* Trend line */}
          <path d={pathD} fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          
          {/* Data points */}
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="4" fill="#f59e0b" stroke="#18181b" strokeWidth="2" />
          ))}
          
          {/* Gradient def */}
          <defs>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#18181b" />
            </linearGradient>
          </defs>
        </svg>
        <div className="flex justify-between text-xs text-zinc-500 mt-1 px-8">
          <span>{data[0]?.date}</span>
          <span>{data[data.length - 1]?.date}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            {language === 'IT' ? 'Carico di Lavoro' : 'Training Load'}
          </h1>
        </div>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-8 text-center text-zinc-500">
            {language === 'IT' ? 'Caricamento...' : 'Loading...'}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Find events with missing RPE
  const pendingRPE = events.filter(e => e.rpe_status === 'MISSING' && canSubmitRPE(e));

  return (
    <div className="space-y-6 fade-in" data-testid="athlete-gps-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
          {language === 'IT' ? 'Carico di Lavoro' : 'Training Load'}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {language === 'IT' ? 'I tuoi dati di carico e performance' : 'Your load and performance data'}
        </p>
      </div>

      {/* Pending RPE Alert */}
      {pendingRPE.length > 0 && (
        <Card className="bg-amber-500/10 border-amber-500/30" data-testid="pending-rpe-alert">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <p className="text-amber-400 font-medium">
                  {language === 'IT' 
                    ? `Hai ${pendingRPE.length} RPE da inserire`
                    : `You have ${pendingRPE.length} RPE to submit`}
                </p>
                <p className="text-sm text-zinc-400">
                  {language === 'IT' 
                    ? 'Inserisci il tuo sforzo percepito per completare i dati'
                    : 'Submit your perceived exertion to complete the data'}
                </p>
              </div>
              <Button
                onClick={() => {
                  setRpeValue('');
                  setShowRPEDialog({ event: pendingRPE[0] });
                }}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                data-testid="submit-rpe-btn"
              >
                {language === 'IT' ? 'Inserisci RPE' : 'Submit RPE'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-zinc-100" data-testid="total-events">{summary.total_events}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'Sessioni (7gg)' : 'Sessions (7d)'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-sm flex items-center justify-center">
                  <Zap className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-emerald-400" data-testid="total-load">{Math.round(summary.total_training_load)}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'Carico Totale' : 'Total Load'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center">
                  <Timer className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-zinc-100" data-testid="total-minutes">{summary.total_minutes}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'Minuti Totali' : 'Total Minutes'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-sm flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-400" data-testid="avg-rpe">{summary.avg_rpe || '-'}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'RPE Medio' : 'Avg RPE'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trend Chart */}
      {history.length > 1 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-100 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-500" />
              {language === 'IT' ? 'Andamento' : 'Trend'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderTrendChart()}
          </CardContent>
        </Card>
      )}

      {/* Events History */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <Activity className="w-5 h-5 text-zinc-500" />
            {language === 'IT' ? 'Storico Sessioni' : 'Session History'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-zinc-800">
            {events.length === 0 ? (
              <div className="p-8 text-center text-zinc-500" data-testid="no-events">
                {language === 'IT' ? 'Nessuna sessione trovata' : 'No sessions found'}
              </div>
            ) : (
              events.map(event => {
                const canSubmit = canSubmitRPE(event);
                
                return (
                  <div 
                    key={event.event_id || event.id} 
                    className="p-4 hover:bg-zinc-800/30 transition-colors cursor-pointer"
                    onClick={() => setShowEventDetail(event)}
                    data-testid={`event-${event.event_id || event.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                        <div className="text-center flex-shrink-0">
                          <div className="text-lg font-bold text-zinc-100">{new Date(event.date).getDate()}</div>
                          <div className="text-xs text-zinc-500 uppercase">
                            {new Date(event.date).toLocaleDateString(language === 'IT' ? 'it-IT' : 'en-US', { month: 'short' })}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-zinc-100 font-medium text-sm sm:text-base leading-snug line-clamp-2 break-words" data-testid={`event-title-${event.event_id || event.id}`}>
                            {event.title || (language === 'IT' ? 'Evento senza titolo' : 'Untitled event')}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <span
                              className="inline-flex items-center rounded border border-zinc-700 bg-zinc-800/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-300"
                              data-testid={`event-type-${event.event_id || event.id}`}
                            >
                              {event.event_type.replace('_', ' ')}
                            </span>
                            <span className="text-xs text-zinc-500 break-words">
                              {event.team_code}
                              {event.start_time ? ` · ${event.start_time}` : ''}
                              {' · '}
                              {getEffectiveMinutes(event) || '-'} min
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                        {event.training_load && (
                          <div className="text-right">
                            <div className="text-lg font-bold text-emerald-400">{event.training_load}</div>
                            <div className="text-xs text-zinc-500">Load</div>
                          </div>
                        )}
                        
                        <div className="text-right">
                          <div className="text-lg font-bold text-amber-400">{event.rpe ?? '-'}</div>
                          <div className="text-xs text-zinc-500">RPE</div>
                        </div>
                        
                        <div className="w-28 flex flex-col items-end gap-1">
                          {event.rpe_status === 'MISSING' && canSubmit ? (
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRpeValue('');
                                setShowRPEDialog({ event });
                              }}
                              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 text-xs min-h-[36px]"
                              data-testid={`submit-rpe-${event.event_id || event.id}`}
                            >
                              {language === 'IT' ? 'Inserisci' : 'Submit'}
                            </Button>
                          ) : (
                            <>
                              {getStatusBadge(event)}
                              {!canSubmit && submissionBlockReason(event) && (
                                <span
                                  className="text-[10px] text-zinc-500 text-right leading-tight max-w-[110px]"
                                  data-testid={`rpe-block-reason-${event.event_id || event.id}`}
                                >
                                  {submissionBlockReason(event)}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* RPE Submit Dialog — mobile-first (touch targets, sticky footer, safe-area) */}
      <Dialog open={!!showRPEDialog} onOpenChange={() => setShowRPEDialog(null)}>
        <DialogContent
          className="bg-zinc-900 border-zinc-800 p-0 gap-0 max-h-[95vh] flex flex-col
                     w-[95vw] sm:w-auto sm:max-w-md
                     max-w-[95vw] rounded-xl overflow-hidden"
          data-testid="rpe-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-800">
            <DialogTitle className="text-zinc-100 text-lg">
              {language === 'IT' ? 'Inserisci RPE' : 'Submit RPE'}
            </DialogTitle>
            {showRPEDialog && (
              <p className="text-xs text-zinc-500 mt-1" data-testid="rpe-dialog-subtitle">
                {language === 'IT' ? 'Quanto è stata intensa la seduta?' : 'How intense was the session?'}
              </p>
            )}
          </DialogHeader>

          {showRPEDialog && (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" data-testid="rpe-dialog-body">
              <div className="text-center space-y-1" data-testid="rpe-event-context">
                <div className="text-base font-semibold text-zinc-100 leading-snug px-2 break-words" data-testid="rpe-event-title">
                  {showRPEDialog.event.title || (language === 'IT' ? 'Evento senza titolo' : 'Untitled event')}
                </div>
                <div className="text-xs text-zinc-400 flex items-center justify-center gap-1.5 flex-wrap">
                  <span
                    className="inline-flex items-center rounded border border-zinc-700 bg-zinc-800/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-300"
                    data-testid="rpe-event-type"
                  >
                    {showRPEDialog.event.event_type.replace('_', ' ')}
                  </span>
                  <span>·</span>
                  <span>{showRPEDialog.event.date}</span>
                  {showRPEDialog.event.start_time && (<><span>·</span><span>{showRPEDialog.event.start_time}</span></>)}
                  <span>·</span>
                  <span>{getEffectiveMinutes(showRPEDialog.event) || '-'} min</span>
                  {showRPEDialog.event.is_match && showRPEDialog.event.post_match_work && showRPEDialog.event.post_match_minutes > 0 && (
                    <span className="text-zinc-500"> ({showRPEDialog.event.match_minutes || 0} + {showRPEDialog.event.post_match_minutes} post)</span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500">{showRPEDialog.event.team_code}</div>
              </div>

              <div className="space-y-3">
                <Label className="text-zinc-400 text-center block text-xs uppercase tracking-wider">
                  {language === 'IT' ? 'Sforzo Percepito (CR10)' : 'Perceived Exertion (CR10)'}
                </Label>
                {/* Large tappable 0-10 grid — min 48px height so it clears the 44×44 iOS touch minimum. */}
                <div className="grid grid-cols-6 gap-2" data-testid="rpe-scale-grid">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((val) => {
                    const active = rpeValue === val.toString();
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setRpeValue(val.toString())}
                        className={`min-h-[48px] rounded-md font-bold text-base transition-colors touch-manipulation ${
                          active
                            ? 'bg-amber-500 text-zinc-950 ring-2 ring-amber-400'
                            : 'bg-zinc-800 text-zinc-300 active:bg-zinc-700 hover:bg-zinc-700'
                        }`}
                        data-testid={`rpe-btn-${val}`}
                        aria-pressed={active}
                        aria-label={`RPE ${val}`}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[11px] text-zinc-500 px-1">
                  <span>0 · {language === 'IT' ? 'Riposo' : 'Rest'}</span>
                  <span>10 · {language === 'IT' ? 'Massimo' : 'Maximum'}</span>
                </div>
              </div>

              {rpeValue && getEffectiveMinutes(showRPEDialog.event) > 0 && (
                <div className="text-center p-3 bg-zinc-950/60 border border-zinc-800 rounded-md" data-testid="rpe-load-preview">
                  <div className="text-xs text-zinc-500 uppercase tracking-wider">
                    {language === 'IT' ? 'Training Load previsto' : 'Estimated Training Load'}
                  </div>
                  <div className="text-2xl font-bold text-emerald-400 mt-1">
                    {parseInt(rpeValue) * getEffectiveMinutes(showRPEDialog.event)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sticky footer — always reachable on short screens, respects iOS safe-area */}
          <DialogFooter
            className="flex-row justify-end gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900
                       pb-[max(env(safe-area-inset-bottom),0.75rem)] sm:pb-3 flex-shrink-0"
            data-testid="rpe-dialog-footer"
          >
            <Button
              variant="outline"
              onClick={() => setShowRPEDialog(null)}
              className="border-zinc-700 min-h-[44px] flex-1 sm:flex-none"
              data-testid="cancel-rpe-btn"
            >
              {language === 'IT' ? 'Annulla' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmitRPE}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 min-h-[44px] flex-1 sm:flex-none font-semibold"
              disabled={!rpeValue}
              data-testid="confirm-rpe-btn"
            >
              {language === 'IT' ? 'Invia RPE' : 'Submit RPE'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Detail Dialog */}
      <Dialog open={!!showEventDetail} onOpenChange={() => setShowEventDetail(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 leading-snug break-words" data-testid="event-detail-title">
              {showEventDetail?.title || (language === 'IT' ? 'Evento senza titolo' : 'Untitled event')}
            </DialogTitle>
            {showEventDetail && (
              <div className="text-xs text-zinc-400 mt-1 flex items-center gap-1.5 flex-wrap" data-testid="event-detail-subtitle">
                <span className="inline-flex items-center rounded border border-zinc-700 bg-zinc-800/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-300">
                  {showEventDetail.event_type.replace('_', ' ')}
                </span>
                <span>·</span>
                <span>{showEventDetail.date}</span>
                {showEventDetail.start_time && (<><span>·</span><span>{showEventDetail.start_time}</span></>)}
              </div>
            )}
          </DialogHeader>
          {showEventDetail && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm">
                  <div className="text-zinc-500">{language === 'IT' ? 'Squadra' : 'Team'}</div>
                  <div className="text-zinc-100 font-semibold">{showEventDetail.team_code}</div>
                </div>
                <div className="p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm">
                  <div className="text-zinc-500">{language === 'IT' ? 'Durata' : 'Duration'}</div>
                  <div className="text-zinc-100 font-semibold">
                    {getEffectiveMinutes(showEventDetail) || '-'} min
                    {showEventDetail.is_match && showEventDetail.post_match_work && showEventDetail.post_match_minutes > 0 && (
                      <span className="text-xs text-zinc-500 ml-1">({showEventDetail.match_minutes || 0} + {showEventDetail.post_match_minutes} post)</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* GPS Metrics if available */}
              {showEventDetail.metrics?.distance_m && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-zinc-400">{language === 'IT' ? 'Metriche GPS' : 'GPS Metrics'}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(showEventDetail.metrics).map(([key, val]) => {
                      if (!val || key === 'session_minutes') return null;
                      return (
                        <div key={key} className="flex justify-between p-2 bg-zinc-950/50 border border-zinc-800 rounded-sm text-sm">
                          <span className="text-zinc-500">{GPS_METRICS_DISPLAY[key] || key}</span>
                          <span className="text-zinc-100 font-mono">{typeof val === 'number' ? val.toLocaleString() : val}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm text-center">
                  <div className="text-2xl font-bold text-amber-400">{showEventDetail.rpe ?? '-'}</div>
                  <div className="text-xs text-zinc-500">RPE</div>
                </div>
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-sm text-center">
                  <div className="text-2xl font-bold text-emerald-400">{showEventDetail.training_load?.toLocaleString() || '-'}</div>
                  <div className="text-xs text-zinc-500">Training Load</div>
                </div>
              </div>
              
              <div className="flex justify-center">
                {getStatusBadge(showEventDetail)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowEventDetail(null)} className="bg-zinc-800 text-zinc-100 hover:bg-zinc-700">
              {language === 'IT' ? 'Chiudi' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AthleteGPSPage;
